import React, { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode.react";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";

import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	InputAdornment,
	MenuItem,
	Paper,
	Tab,
	Tabs,
	TextField,
	Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";

const COUNTRY_CODES = [
	{ code: "505", label: "Nicaragua (+505)" },
	{ code: "506", label: "Costa Rica (+506)" },
	{ code: "502", label: "Guatemala (+502)" },
	{ code: "504", label: "Honduras (+504)" },
	{ code: "503", label: "El Salvador (+503)" },
	{ code: "507", label: "Panamá (+507)" },
	{ code: "52", label: "México (+52)" },
	{ code: "57", label: "Colombia (+57)" },
	{ code: "1", label: "EE.UU. / Canadá (+1)" },
	{ code: "34", label: "España (+34)" },
	{ code: "55", label: "Brasil (+55)" },
];

const useStyles = makeStyles(theme => ({
	paper: {
		padding: theme.spacing(2),
		minWidth: 320,
		textAlign: "center",
	},
	codeBox: {
		marginTop: theme.spacing(2),
		padding: theme.spacing(2),
		letterSpacing: 4,
		fontSize: 28,
		fontWeight: 700,
	},
	field: {
		marginTop: theme.spacing(2),
	},
	hint: {
		marginTop: theme.spacing(1),
	},
}));

const QrcodeModal = ({ open, onClose, whatsAppId, qrcode: propQrcode }) => {
	const classes = useStyles();
	const [qrCode, setQrCode] = useState("");
	const [tab, setTab] = useState(0);
	const [countryCode, setCountryCode] = useState("505");
	const [phone, setPhone] = useState("");
	const [pairingCode, setPairingCode] = useState("");
	const [loadingCode, setLoadingCode] = useState(false);

	const fullNumber = useMemo(() => {
		const local = String(phone || "").replace(/\D/g, "");
		return `${countryCode}${local}`;
	}, [countryCode, phone]);

	useEffect(() => {
		const fetchSession = async () => {
			if (!whatsAppId) return;
			try {
				const { data } = await api.get(`/whatsapp/${whatsAppId}`);
				setQrCode(data.qrcode || "");
			} catch (err) {
				toastError(err);
			}
		};
		if (open) {
			fetchSession();
			setPairingCode("");
			setTab(0);
		}
	}, [whatsAppId, open]);

	useEffect(() => {
		if (propQrcode !== undefined) {
			setQrCode(propQrcode || "");
		}
	}, [propQrcode]);

	useEffect(() => {
		if (!whatsAppId || !open) return;
		const socket = openSocket();
		if (!socket) return undefined;

		const onSession = data => {
			if (data.action === "update" && data.session.id === whatsAppId) {
				setQrCode(data.session.qrcode || "");
				if (data.session.status === "CONNECTED") {
					onClose();
				}
			}
		};

		const onWhatsapp = data => {
			if (
				data.action === "update" &&
				data.whatsapp?.id === whatsAppId &&
				data.whatsapp.status === "CONNECTED"
			) {
				onClose();
			}
		};

		socket.on("whatsappSession", onSession);
		socket.on("whatsapp", onWhatsapp);

		return () => {
			socket.off("whatsappSession", onSession);
			socket.off("whatsapp", onWhatsapp);
		};
	}, [whatsAppId, onClose, open]);

	const handleRequestCode = async () => {
		const local = String(phone || "").replace(/\D/g, "");
		if (local.length < 8) {
			toast.error(i18n.t("qrCode.invalidPhone"));
			return;
		}
		setLoadingCode(true);
		try {
			const { data } = await api.post(`/whatsapp/${whatsAppId}/auth-code`, {
				phoneNumber: fullNumber,
			});
			setPairingCode(data.code || "");
		} catch (err) {
			toastError(err);
		} finally {
			setLoadingCode(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
			<DialogTitle>{i18n.t("qrCode.title")}</DialogTitle>
			<DialogContent>
				<Tabs
					value={tab}
					onChange={(_, value) => setTab(value)}
					indicatorColor="primary"
					textColor="primary"
					variant="fullWidth"
				>
					<Tab label={i18n.t("qrCode.tabs.qr")} />
					<Tab label={i18n.t("qrCode.tabs.code")} />
				</Tabs>
				{tab === 0 && (
					<Paper elevation={0} className={classes.paper}>
						<Typography color="primary" gutterBottom>
							{i18n.t("qrCode.message")}
						</Typography>
						{qrCode ? (
							<QRCode value={qrCode} size={256} />
						) : (
							<Typography>{i18n.t("qrCode.waiting")}</Typography>
						)}
					</Paper>
				)}
				{tab === 1 && (
					<Paper elevation={0} className={classes.paper}>
						<Typography color="primary" gutterBottom>
							{i18n.t("qrCode.codeMessage")}
						</Typography>
						<TextField
							select
							fullWidth
							label={i18n.t("qrCode.country")}
							value={countryCode}
							onChange={e => setCountryCode(e.target.value)}
							className={classes.field}
						>
							{COUNTRY_CODES.map(item => (
								<MenuItem key={item.code} value={item.code}>
									{item.label}
								</MenuItem>
							))}
						</TextField>
						<TextField
							fullWidth
							label={i18n.t("qrCode.phone")}
							value={phone}
							onChange={e => setPhone(e.target.value.replace(/[^\d]/g, ""))}
							className={classes.field}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">+{countryCode}</InputAdornment>
								),
							}}
						/>
						<Box mt={2}>
							<Button
								variant="contained"
								color="primary"
								onClick={handleRequestCode}
								disabled={loadingCode}
							>
								{loadingCode ? (
									<CircularProgress size={22} />
								) : (
									i18n.t("qrCode.requestCode")
								)}
							</Button>
						</Box>
						{pairingCode && (
							<Paper variant="outlined" className={classes.codeBox}>
								{pairingCode}
							</Paper>
						)}
						<Typography variant="body2" color="textSecondary" className={classes.hint}>
							{i18n.t("qrCode.codeHint")}
						</Typography>
					</Paper>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default React.memo(QrcodeModal);
